import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PerfilesService } from '@services/perfiles/perfiles.service';
import { MenuesService } from '@services/menues/menues.service';
import { StorageService } from '@services/shared/storage.service';
import { Perfil } from '@interfaces/interface-bakend/seguridad/acl/perfil.interface';
import { TiposWebKeys } from '@enum/tiposweb-keys.enum';
import { DiccionarioKeys } from '@enum/idiomas-diccionario-keys.enum';
import { PopUpModalCustomService } from '@services/shared/popup-modal-custom.service';
import { MenuTreeNode } from '@interfaces/interface-bakend/seguridad/acl/menu.interface';

interface TreeNode {
    _id: string;
    name: string;
    rawName: string;
    redirectTo: string;
    level: number;
    order: number;
    expanded: boolean;
    children: TreeNode[];
    parentId: string | null;
}

@Component({
    selector: 'app-permisos-administracion',
    templateUrl: './permisos-administracion.component.html',
    styleUrls: ['./permisos-administracion.component.scss'],
})
export class PermisosAdministracionComponent implements OnInit, OnDestroy {

    flagLoaded: boolean = false;

    perfiles: Perfil[] = [];
    selectedPerfil: Perfil | null = null;
    selectedCopyFrom: Perfil | null = null;

    treeRoots: TreeNode[] = [];
    checkedIds = new Set<string>();

    private nodeMap = new Map<string, TreeNode>();

    constructor(
        private router: Router,
        private perfilesService: PerfilesService,
        private menuesService: MenuesService,
        private storageService: StorageService,
        private modalCustom: PopUpModalCustomService,
    ) {}

    ngOnDestroy(): void {
        this.modalCustom.cerrar();
    }

    async ngOnInit(): Promise<void> {
        const perfilesResp = await this.perfilesService.findAllPerfiles();
        this.perfiles = (perfilesResp?.response || perfilesResp?.data || []);

        this.treeRoots = await this.buildTree();

        if (this.perfiles.length > 0) {
            this.seleccionarPerfil(this.perfiles[0]);
        }

        this.flagLoaded = true;
    }

    /**
     * Arma el arbol con una sola llamada: el backend devuelve el bosque completo
     * ya anidado y con los nombres traducidos.
     */
    private async buildTree(): Promise<TreeNode[]> {
        this.nodeMap.clear();

        const tree = await this.menuesService.getMenuTree(
            TiposWebKeys.WEB,
            DiccionarioKeys.I18NFILE,
            'es',
        );

        return (tree || []).map((node) => this.toTreeNode(node));
    }

    private toTreeNode(node: MenuTreeNode): TreeNode {
        const mapped: TreeNode = {
            _id: node._id,
            name: node.name || '',
            rawName: node.rawName || node.name || '',
            redirectTo: node.redirectTo || '',
            level: node.level || 1,
            order: node.order || 0,
            expanded: false,
            children: [],
            parentId: node.parent || null,
        };

        this.nodeMap.set(mapped._id, mapped);
        mapped.children = (node.children || []).map((child) => this.toTreeNode(child));
        return mapped;
    }

    private collectDescendants(node: TreeNode): string[] {
        const ids: string[] = [];
        const walk = (n: TreeNode) => {
            ids.push(n._id);
            n.children.forEach(walk);
        };
        node.children.forEach(walk);
        return ids;
    }

    seleccionarPerfil(perfil: Perfil): void {
        this.selectedPerfil = perfil;
        this.checkedIds = new Set(perfil.menuWeb || []);
    }

    copiarReglas(perfil: Perfil): void {
        if (!perfil) { return; }
        this.checkedIds = new Set(perfil.menuWeb || []);
        this.selectedCopyFrom = null;
    }

    limpiarCambios(): void {
        if (!this.selectedPerfil) { return; }
        this.checkedIds = new Set(this.selectedPerfil.menuWeb || []);
        this.selectedCopyFrom = null;
    }

    toggleExpand(node: TreeNode): void {
        node.expanded = !node.expanded;
    }

    isChecked(node: TreeNode): boolean {
        return this.checkedIds.has(node._id);
    }

    isIndeterminate(node: TreeNode): boolean {
        if (!node.children.length) { return false; }
        const directIds = node.children.map((c) => c._id);
        const checkedDirect = directIds.filter((id) => this.checkedIds.has(id));
        const allDescendants = this.collectDescendants(node);
        const checkedAll = allDescendants.filter((id) => this.checkedIds.has(id));
        if (checkedDirect.length === 0 && checkedAll.length === 0) { return false; }
        if (checkedAll.length === allDescendants.length && this.isChecked(node)) { return false; }
        return checkedDirect.length > 0 && checkedDirect.length < directIds.length
            || allDescendants.some((id) => this.checkedIds.has(id));
    }

    onCheck(node: TreeNode, checked: boolean): void {
        const allDescendants = this.collectDescendants(node);

        if (checked) {
            this.checkedIds.add(node._id);
            allDescendants.forEach((id) => this.checkedIds.add(id));
        } else {
            this.checkedIds.delete(node._id);
            allDescendants.forEach((id) => this.checkedIds.delete(id));
        }

        this.updateAncestors(node);
    }

    updateAncestors(node: TreeNode): void {
        let current: TreeNode | undefined = node;

        while (current && current.parentId) {
            const parent = this.nodeMap.get(current.parentId);
            if (!parent) { break; }

            const directChildren = parent.children;
            const allChecked = directChildren.every((c) => this.checkedIds.has(c._id));
            const allDescendants = this.collectDescendants(parent);
            const anyChecked = allDescendants.some((id) => this.checkedIds.has(id));

            if (allChecked) {
                this.checkedIds.add(parent._id);
            } else if (anyChecked) {
                this.checkedIds.delete(parent._id);
            } else {
                this.checkedIds.delete(parent._id);
            }

            current = parent;
        }
    }

    async guardar(): Promise<void> {
        if (!this.selectedPerfil) { return; }

        const menuWeb = [...this.checkedIds];

        const payload = {
            _id: this.selectedPerfil._id,
            key: this.selectedPerfil.key,
            menuWeb,
        };

        try {
            await this.perfilesService.updatePerfil(this.selectedPerfil._id, payload);

            const idx = this.perfiles.findIndex((p) => p._id === this.selectedPerfil!._id);
            if (idx !== -1) {
                this.perfiles[idx].menuWeb = menuWeb as any;
            }

            this.storageService.clearMenuCache('es', this.selectedPerfil!._id);

            this.modalCustom.abrirPopup({
                titulo: 'Permisos actualizados',
                mensaje: 'Los permisos del rol se guardaron correctamente.',
                tipo: 'success',
            });
        } catch {
            this.modalCustom.abrirPopup({
                titulo: 'Error al guardar',
                mensaje: 'Ocurrió un error al actualizar los permisos. Intente nuevamente.',
                tipo: 'error',
            });
        }
    }

    volver(): void {
        this.router.navigateByUrl('/menu-seguridad');
    }
}
